// File: src/components/trends/ArtistInfoCard.js

import React, { useEffect, useState, useRef, useLayoutEffect } from "react";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { getDisplayArtistNames } from "../../Utils/song";
import { useMusic } from "../../context/MusicContext";

const ArtistInfoCard = ({ currentSong }) => {
  const { audioRef } = useMusic();
  const [artistDetails, setArtistDetails] = useState(null);

  // --- SCRUB BAR REFS & STATE ---
  const progressBarRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPercent, setDragPercent] = useState(0);

  // --- BIO OVERFLOW & EXPANSION REFS & STATE ---
  const bioTextRef = useRef(null);
  const [isBioExpanded, setIsBioExpanded] = useState(false);
  const [hasBioOverflow, setHasBioOverflow] = useState(false);

  // Fetch artist details
  useEffect(() => {
    const fetchArtistDetails = async () => {
      if (!currentSong?.artistId) {
        setArtistDetails(null);
        return;
      }

      try {
        const db = getFirestore();
        const artistRef = doc(db, "artist_accounts", currentSong.artistId);
        const snapshot = await getDoc(artistRef);

        if (snapshot.exists()) {
          setArtistDetails(snapshot.data());
        } else {
          setArtistDetails(null);
        }
      } catch (err) {
        console.error("Error fetching artist card details:", err);
      }
    };

    fetchArtistDetails();
    setIsBioExpanded(false);
  }, [currentSong?.artistId, currentSong?.id]);

  // Synchronize audio playback time & duration
  useEffect(() => {
    const audio = audioRef?.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      if (!isDragging) {
        setCurrentTime(audio.currentTime);
      }
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration || 0);
    };

    if (audio.duration) {
      setDuration(audio.duration);
    }

    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
    };
  }, [audioRef, isDragging]);

  // Determine bio content
  const displayBio = currentSong?.description?.trim()
    ? currentSong.description
    : artistDetails?.bio?.trim()
    ? artistDetails.bio
    : null;

  // Accurately check if text overflows the 2-line clamp
  useLayoutEffect(() => {
    if (!bioTextRef.current || !displayBio) {
      setHasBioOverflow(false);
      return;
    }

    // Only compute overflow when not expanded to test the 2-line threshold
    if (!isBioExpanded) {
      const el = bioTextRef.current;
      setHasBioOverflow(el.scrollHeight > el.clientHeight);
    }
  }, [displayBio, isBioExpanded]);

  // --- SCRUBBER HELPERS & HANDLERS ---
  const handleDrag = (clientX) => {
    if (!progressBarRef.current) return;
    const rect = progressBarRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const ratio = rect.width > 0 ? x / rect.width : 0;
    const newTime = ratio * (duration || 0);
    setDragPercent(ratio * 100);
    setCurrentTime(newTime);
  };

  const handleMouseDown = (e) => {
    if (!audioRef?.current || !duration) return;
    setIsDragging(true);
    handleDrag(e.clientX);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    handleDrag(e.clientX);
  };

  const handleMouseUp = () => {
    if (isDragging && audioRef?.current && duration) {
      audioRef.current.currentTime = (dragPercent / 100) * duration;
    }
    setIsDragging(false);
  };

  if (!currentSong) return null;

  const currentPercent = duration
    ? isDragging
      ? dragPercent
      : (currentTime / duration) * 100
    : 0;

  return (
    <div
      /* pb-24 lifts the scrub bar safely above BottomMenuBar and its touch zones */
      className="absolute bottom-0 left-0 right-0 pb-16 sm:pb-28 z-30 pointer-events-none select-none flex flex-col justify-end"
      style={{
        background:
          "linear-gradient(to top, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.95) 30%, rgba(0, 0, 0, 0.75) 60%, rgba(0, 0, 0, 0.35) 85%, transparent 100%)",
        minHeight: "22rem",
      }}
    >
      {/* 1. TEXT SECTION */}
      <div className="relative z-20 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)] px-4 pr-16 mb-3">
        {/* Track Title */}
        <div className="text-lg font-bold leading-snug truncate">
          {currentSong.title || "Untitled"}
        </div>

        {/* Artist Name */}
        <div className="text-sm italic text-gray-200">
          By {currentSong ? getDisplayArtistNames(currentSong) : "Unknown Artist"}
        </div>

        {/* Location */}
        {artistDetails?.city && artistDetails?.state && (
          <div className="text-xs text-gray-300 mt-0.5">
            {artistDetails.city}, {artistDetails.state}
          </div>
        )}

        {/* Expandable Bio */}
        {displayBio && (
          <div className="mt-2 pointer-events-auto">
            <p
              ref={bioTextRef}
              className={`text-xs text-gray-200 leading-relaxed transition-all duration-200 ${
                isBioExpanded
                  ? "max-h-40 overflow-y-auto pr-1 no-scrollbar"
                  : "line-clamp-2"
              }`}
            >
              {displayBio}
            </p>

            {/* Only show toggle button when text actually overflows */}
            {(hasBioOverflow || isBioExpanded) && (
              <button
                type="button"
                onClick={() => setIsBioExpanded((prev) => !prev)}
                className="text-[11px] text-[#ff00cc] font-semibold mt-1 underline focus:outline-none block"
              >
                {isBioExpanded ? "Show less" : "Read more"}
              </button>
            )}
          </div>
        )}
      </div>

      {/* 
        2. FULL-WIDTH SCRUB BAR:
        - `h-8` touch-target container ensures fingers easily grab the scrubber.
        - The visual track stays at `h-1.5` flat rectangular edge.
      */}
      <div className="w-full relative h-8 flex items-center pointer-events-auto">
        <div
          ref={progressBarRef}
          className="relative w-full h-1.5 bg-white/20 rounded-none cursor-pointer"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={(e) => handleMouseDown(e.touches[0])}
          onTouchMove={(e) => handleMouseMove(e.touches[0])}
          onTouchEnd={handleMouseUp}
        >
          {/* Active Fill Track */}
          <div
            className="absolute top-0 left-0 h-full bg-[#ff00cc] rounded-none"
            style={{ width: `${currentPercent}%` }}
          />

          {/* Scrubber Knob */}
          <div
            className="absolute w-3 h-3 bg-white rounded-full shadow-md z-10"
            style={{
              top: "50%",
              left: `${currentPercent}%`,
              transform: "translate(-50%, -50%)",
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ArtistInfoCard;